import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const NAV_LINKS = [
  { to: '/gallery',      label: 'Work' },
  { to: '/services',     label: 'Services' },
  { to: '/locations',    label: 'Locations' },
  { to: '/testimonials', label: 'Stories' },
  { to: '/contact',      label: 'Contact' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setIsOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <>
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0,   opacity: 1 }}
        transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        aria-label="Primary"
        className="fixed top-0 left-0 right-0 z-50 bg-ink/95 backdrop-blur-sm"
      >
        <div className="mx-auto px-8 md:px-14 lg:px-20">
          <div className="flex justify-between items-center h-16 md:h-20">

            {/* Logo */}
            <Link
              to="/"
              className="font-serif text-[1.1rem] font-light tracking-wide text-white leading-none"
            >
              George &amp; Co.
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-10">
              {NAV_LINKS.map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  className={`label-sm transition-opacity duration-400 ${
                    location.pathname === to
                      ? 'text-white opacity-100'
                      : 'text-white/55 hover:text-white hover:opacity-100'
                  }`}
                >
                  {label}
                </Link>
              ))}
            </div>

            {/* Hamburger */}
            <button
              onClick={() => setIsOpen(v => !v)}
              className="md:hidden flex flex-col gap-[5px] p-2"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              <span
                className={`block w-6 h-px bg-white transition-all duration-500 origin-center ${
                  isOpen ? 'rotate-45 translate-y-[6px]' : ''
                }`}
              />
              <span
                className={`block h-px bg-white transition-all duration-300 ${
                  isOpen ? 'w-0 opacity-0' : 'w-4'
                }`}
              />
              <span
                className={`block w-6 h-px bg-white transition-all duration-500 origin-center ${
                  isOpen ? '-rotate-45 -translate-y-[6px]' : ''
                }`}
              />
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Full-screen mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45 }}
            className="fixed inset-0 z-40 bg-ink flex flex-col justify-center px-10 md:px-14"
          >
            <nav aria-label="Mobile" className="space-y-7">
              <motion.div
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.4, delay: 0 }}
              >
                <Link
                  to="/"
                  onClick={() => setIsOpen(false)}
                  className={`heading-display text-[2.6rem] leading-tight transition-opacity duration-300 ${
                    location.pathname === '/' ? 'text-white' : 'text-white/60 hover:text-white/75'
                  }`}
                >
                  Home
                </Link>
              </motion.div>

              {NAV_LINKS.map(({ to, label }, i) => (
                <motion.div
                  key={to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.4, delay: (i + 1) * 0.06 }}
                >
                  <Link
                    to={to}
                    onClick={() => setIsOpen(false)}
                    className={`heading-display text-[2.6rem] leading-tight transition-opacity duration-300 ${
                      location.pathname === to ? 'text-white' : 'text-white/60 hover:text-white/75'
                    }`}
                  >
                    {label}
                  </Link>
                </motion.div>
              ))}
            </nav>

            <div className="absolute bottom-10 left-10 right-10 flex justify-between items-end">
              <p className="label-sm text-white/60">George &amp; Co. — Est. 2019</p>
              <a
                href="mailto:info@georgeandco.co.za"
                className="label-sm text-white/60 hover:text-white/65 transition-colors duration-300"
              >
                info@georgeandco.co.za
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
